import { AlertCircle, FileText, FolderUp, Loader2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import type { InputSlot } from './data-slot-uploader';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useDicomImport } from '@/hooks';
import type { DicomImportResult, DicomMetadata, SeriesInfo } from '@/lib/dicom';
import { formatFileSize } from '@/lib/file-utils';
import { cn } from '@/lib/utils';

interface DicomVolumeImporterProps {
    slot: InputSlot;
    /**
     * Fires when an import becomes ready, or with null when the user
     * resets the widget.
     */
    onChange: (result: DicomImportResult | null) => void;
    /**
     * Patient metadata pre-fill hook. Fires once per successful import,
     * after `onChange`. Parents typically use this to seed empty form
     * fields (name, DoB, sex) without overwriting user input.
     */
    onMetadata?: (metadata: DicomMetadata) => void;
    disabled?: boolean;
}

/**
 * Browser DICOM import widget for VOLUME slots. Replaces the legacy file
 * picker with a folder picker + worker-driven parse → series-pick →
 * reconstruct → preview pipeline. The reconstructed MHA + preview MHA +
 * SHA-256 are surfaced via `onChange` so the parent can drive the upload.
 */
export function DicomVolumeImporter({ slot, onChange, onMetadata, disabled }: DicomVolumeImporterProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const importer = useDicomImport();
    const { state, progress, seriesList, result, metadata, error, start, selectSeries, reset } = importer;

    const isRequired = slot.required !== false;
    const busy = state === 'parsing' || state === 'reconstructing';
    const lastNotifiedResult = useRef<DicomImportResult | null>(null);

    // Surface the ready result to the parent once per successful import.
    useEffect(() => {
        if (state === 'ready' && result && result !== lastNotifiedResult.current) {
            lastNotifiedResult.current = result;
            onChange(result);
            if (onMetadata && metadata) onMetadata(metadata);
        }
    }, [state, result, metadata, onChange, onMetadata]);

    const handlePick = useCallback(async () => {
        // Prefer the File System Access API when available (Chromium): it
        // shows a single permission prompt for the directory and skips the
        // "you are about to upload N files" warning that `webkitdirectory`
        // triggers in Edge/Chrome. Fall back to the hidden input for
        // browsers without it (Safari, Firefox).
        if (typeof window !== 'undefined' && 'showDirectoryPicker' in window) {
            try {
                const files = await pickDirectoryViaFileSystemAccess();
                if (files.length > 0) await start(files);
            } catch (err) {
                // User cancellation surfaces as AbortError — silent ignore.
                if (!(err instanceof DOMException && err.name === 'AbortError')) {
                    throw err;
                }
            }
            return;
        }
        inputRef.current?.click();
    }, [start]);

    const handleFiles = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const fileList = event.target.files;
            if (!fileList || fileList.length === 0) return;
            const files = Array.from(fileList);
            // Reset input so the same folder can be re-selected after a reset.
            if (inputRef.current) inputRef.current.value = '';
            await start(files);
        },
        [start]
    );

    const handleReset = useCallback(() => {
        lastNotifiedResult.current = null;
        reset();
        onChange(null);
    }, [reset, onChange]);

    return (
        <div className="space-y-3">
            <SlotHeader slot={slot} isRequired={isRequired} />

            <input
                ref={inputRef}
                type="file"
                multiple
                // webkitdirectory is non-standard but supported by Chromium/WebKit.
                // The TS DOM lib does not declare it; pass via spread to satisfy types.
                {...({ webkitdirectory: '' } as unknown as Record<string, string>)}
                onChange={handleFiles}
                className="hidden"
                disabled={disabled || busy}
            />

            {state === 'idle' && <DropZone disabled={disabled} onClick={handlePick} />}

            {(state === 'parsing' || state === 'reconstructing') && (
                <ProgressCard
                    label={state === 'parsing' ? 'Reading DICOM files…' : 'Reconstructing volume…'}
                    progress={progress?.fraction ?? null}
                />
            )}

            {state === 'series-select' && (
                <SeriesPicker series={seriesList} onSelect={selectSeries} disabled={disabled} />
            )}

            {state === 'ready' && result && (
                <ReadyCard result={result} onReset={handleReset} disabled={disabled} />
            )}

            {state === 'error' && (
                <ErrorCard
                    message={error?.message ?? 'Unknown error'}
                    onReset={handleReset}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

function SlotHeader({ slot, isRequired }: { slot: InputSlot; isRequired: boolean }) {
    return (
        <div>
            <h4 className="text-sm font-medium">
                {slot.name}
                {isRequired ? (
                    <span className="text-destructive ml-1">*</span>
                ) : (
                    <span className="text-muted-foreground ml-1 text-xs font-normal">(optional)</span>
                )}
            </h4>
            {slot.description && <p className="text-xs text-muted-foreground mt-0.5">{slot.description}</p>}
            <p className="text-xs text-muted-foreground mt-1">
                Select the folder containing the DICOM series for this slot.
            </p>
        </div>
    );
}

function DropZone({ disabled, onClick }: { disabled?: boolean; onClick: () => void }) {
    return (
        <div
            onClick={disabled ? undefined : onClick}
            className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                disabled
                    ? 'border-muted bg-muted/50 cursor-not-allowed'
                    : 'border-muted-foreground/25 hover:border-primary/50 cursor-pointer'
            )}
        >
            <FolderUp className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
                {disabled ? 'Upload disabled' : 'Click to select a DICOM folder'}
            </p>
        </div>
    );
}

function ProgressCard({ label, progress }: { label: string; progress: number | null }) {
    const percent = progress === null ? null : Math.round(progress * 100);
    return (
        <Card>
            <CardContent className="space-y-3 py-4">
                <div className="flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <p className="text-sm">{label}</p>
                    {percent !== null && (
                        <span className="ml-auto text-xs text-muted-foreground">{percent}%</span>
                    )}
                </div>
                {percent !== null && <Progress value={percent} />}
            </CardContent>
        </Card>
    );
}

function SeriesPicker({
    series,
    onSelect,
    disabled,
}: {
    series: SeriesInfo[];
    onSelect: (uid: string) => void;
    disabled?: boolean;
}) {
    return (
        <Card>
            <CardContent className="space-y-2 py-4">
                <p className="text-sm font-medium">Pick a series</p>
                <p className="text-xs text-muted-foreground">
                    Found {series.length} series in the selected folder.
                </p>
                <ul className="divide-y">
                    {series.map((s) => (
                        <li key={s.seriesInstanceUid} className="flex items-center gap-3 py-2">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">
                                    {s.seriesDescription ?? s.seriesInstanceUid}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {s.modality ?? '?'} · {s.instanceCount} instance
                                    {s.instanceCount === 1 ? '' : 's'}
                                </p>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={disabled}
                                onClick={() => onSelect(s.seriesInstanceUid)}
                            >
                                Select
                            </Button>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}

function ReadyCard({
    result,
    onReset,
    disabled,
}: {
    result: DicomImportResult;
    onReset: () => void;
    disabled?: boolean;
}) {
    const [x, y, z] = result.dimensions;
    return (
        <Card>
            <CardContent className="flex items-center gap-3 py-4">
                <FileText className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Volume ready</p>
                    <p className="text-xs text-muted-foreground">
                        {x} × {y} × {z} voxels · {formatFileSize(result.mhaBuffer.byteLength)}
                    </p>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={onReset} disabled={disabled}>
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Replace
                </Button>
            </CardContent>
        </Card>
    );
}

/**
 * Walk a directory handle from the File System Access API and collect
 * every File inside it (recursively). Files are returned in iteration
 * order; the worker then filters / groups them by DICOM tags.
 */
async function pickDirectoryViaFileSystemAccess(): Promise<File[]> {
    const directoryHandle = await (
        window as unknown as {
            showDirectoryPicker: (options?: {
                mode?: 'read' | 'readwrite';
            }) => Promise<FileSystemDirectoryHandle>;
        }
    ).showDirectoryPicker({ mode: 'read' });

    const collected: File[] = [];
    await walkDirectory(directoryHandle, collected);
    return collected;
}

async function walkDirectory(directory: FileSystemDirectoryHandle, out: File[]): Promise<void> {
    // entries() is an async iterator returning [name, handle] pairs.
    const iterator = (
        directory as unknown as {
            entries: () => AsyncIterableIterator<[string, FileSystemHandle]>;
        }
    ).entries();
    for await (const [, handle] of iterator) {
        if (handle.kind === 'file') {
            const file = await (handle as FileSystemFileHandle).getFile();
            out.push(file);
        } else if (handle.kind === 'directory') {
            await walkDirectory(handle as FileSystemDirectoryHandle, out);
        }
    }
}

function ErrorCard({
    message,
    onReset,
    disabled,
}: {
    message: string;
    onReset: () => void;
    disabled?: boolean;
}) {
    return (
        <Card>
            <CardContent className="flex items-center gap-3 py-4">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">DICOM import failed</p>
                    <p className="text-xs text-muted-foreground truncate">{message}</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={onReset} disabled={disabled}>
                    Try again
                </Button>
            </CardContent>
        </Card>
    );
}
